import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const MeetingRoom = () => {
    const { meetingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [userName, setUserName] = useState('');
    const [isJoined, setIsJoined] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiLoaded, setApiLoaded] = useState(false);
    const jitsiContainerRef = useRef(null);
    const jitsiApiRef = useRef(null);

    const jitsiDomain = process.env.REACT_APP_JITSI_DOMAIN || 'meet.owhs.nl';
    const jitsiPort = process.env.REACT_APP_JITSI_PORT !== undefined && process.env.REACT_APP_JITSI_PORT !== ''
        ? process.env.REACT_APP_JITSI_PORT
        : (process.env.NODE_ENV === 'development' ? '8444' : '');
    const jitsiProtocol = process.env.REACT_APP_JITSI_PROTOCOL || 'https';

    // Extract JWT token and name from URL parameters
    const getJWTFromUrl = () => {
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('jwt');
    };

    const getNameFromUrl = () => {
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('name');
    };

    const jwtToken = getJWTFromUrl();
    const nameFromUrl = getNameFromUrl();

    useEffect(() => {
        if (nameFromUrl) {
            setUserName(nameFromUrl);
        }
    }, [nameFromUrl]);

    // Load Jitsi Meet External API script
    useEffect(() => {
        if (window.JitsiMeetExternalAPI) {
            setApiLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `${jitsiProtocol}://${jitsiDomain}${jitsiPort ? ':' + jitsiPort : ''}/external_api.js`;
        script.async = true;
        script.onload = () => {
            console.log('Jitsi External API script loaded successfully');
            setApiLoaded(true);
        };
        script.onerror = () => {
            console.error('Failed to load Jitsi External API script');
            setError('Er is iets misgegaan bij het laden van de meeting interface.');
        };
        document.head.appendChild(script);

        return () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, []);

    // Initialize Jitsi Meeting with External API when joined
    useEffect(() => {
        if (!isJoined || !apiLoaded || !window.JitsiMeetExternalAPI || !jitsiContainerRef.current) return;

        const domain = jitsiDomain + (jitsiPort ? ':' + jitsiPort : '');
        const options = {
            roomName: meetingId,
            parentNode: jitsiContainerRef.current,
            userInfo: {
                displayName: userName.trim()
            },
            configOverwrite: {
                prejoinPageEnabled: false,
                requireDisplayName: false,
                startWithVideoMuted: false,
                startWithAudioMuted: false,
                resolution: 720,
                enableLobbyChat: true,
                enableLobby: true,
                lobbyEnabled: true,
            },
            interfaceConfigOverwrite: {
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                TOOLBAR_BUTTONS: jwtToken ? undefined : [
                    'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
                    'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
                    'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
                    'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
                    'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone'
                ],
                SHOW_LOBBY_BUTTON: true,
            }
        };

        if (jwtToken) {
            options.jwt = jwtToken;
            console.log('Using JWT token for authentication');
        } else {
            console.log('No JWT token - joining as guest');
        }

        console.log('options', options);
        console.log('Initializing Jitsi Meet External API with domain:', domain, 'options:', options);

        try {
            const api = new window.JitsiMeetExternalAPI(domain, options);

            jitsiApiRef.current = api;

            api.addEventListener('videoConferenceLeft', handleLeaveMeeting);
            api.addEventListener('hangup', handleLeaveMeeting);
            api.addEventListener('readyToClose', handleLeaveMeeting);
            api.addEventListener('conferenceEnded', handleLeaveMeeting);
            api.addEventListener('conferenceLeft', handleLeaveMeeting);

            api.addEventListener('knockingParticipant', (event) => {
                console.log(event);
            });

            console.log('Jitsi External API initialized successfully with event listeners');

        } catch (error) {
            console.error('Error initializing Jitsi External API:', error);
            setError('Er is iets misgegaan bij het initialiseren van de meeting interface');
            setLoading(false);
        }

        return () => {
            if (jitsiApiRef.current && typeof jitsiApiRef.current.dispose === 'function') {
                try {
                    jitsiApiRef.current.dispose();
                } catch (e) {
                    console.log('Error disposing Jitsi API:', e);
                }
                jitsiApiRef.current = null;
            }
        };
    }, [isJoined, apiLoaded, meetingId, userName, jwtToken, jitsiDomain, jitsiPort, jitsiProtocol]);

    const handleJoinMeeting = async (e) => {
        e.preventDefault();
        if (!userName.trim()) return;

        setLoading(true);
        setError(null);

        try {
            if (!apiLoaded || !window.JitsiMeetExternalAPI) {
                setError('De meet API is niet beschikbaar. Gelieve te verversen en opnieuw te proberen.');
                setLoading(false);
                return;
            }

            setIsJoined(true);
            setLoading(false);

        } catch (err) {
            console.error('Error joining meeting:', err);
            setError(`Er is iets misgegaan bij het deelnemen aan de meeting: ${err.message}`);
            setLoading(false);
        }
    };

    const handleLeaveMeeting = () => {
        console.log('Leaving meeting and returning to home page');

        if (jitsiApiRef.current && typeof jitsiApiRef.current.dispose === 'function') {
            try {
                jitsiApiRef.current.dispose();
            } catch (e) {
                console.log('Error disposing Jitsi API:', e);
            }
            jitsiApiRef.current = null;
        }

        navigate('/');
    };

    if (!meetingId) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid Meeting</h1>
                    <p className="text-gray-600 mb-4">No meeting ID provided</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Terug
                    </button>
                </div>
            </div>
        );
    }


    if (isJoined) {
        return (
            <div className="fixed inset-0 bg-black">
                <div
                    ref={jitsiContainerRef}
                    className="w-full h-full relative"
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="max-w-md w-full space-y-8 p-8">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Deelnemen aan meeting "{meetingId}"
                    </h1>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Voorafgaand aan deelname:</h3>
                    <ul className="text-xs text-blue-700 space-y-1">
                        <li>- Je wordt gevraagd om toegang tot je camera en microfoon</li>
                        <li>- Kies een rustige omgeving voor de beste ervaring</li>
                    </ul>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="text-red-800">
                            <h3 className="font-medium mb-2">Fout bij het verbinden</h3>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-md p-6">
                    <form onSubmit={handleJoinMeeting} className="space-y-4">
                        <div>
                            <label htmlFor="userName" className="block text-xl font-medium text-gray-700 mb-2">
                                Wat is je naam?
                            </label>
                            <input
                                type="text"
                                id="userName"
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                placeholder="Naam"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!userName.trim() || loading || !apiLoaded}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Deelnemen...' : !apiLoaded ? 'Laden...' : 'Deelnemen'}
                        </button>
                    </form>

                    <div className="mt-4 text-center">
                        <button
                            onClick={() => navigate('/')}
                            className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                            ← Terug
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MeetingRoom;